import { c as _c } from "react/compiler-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:no-underline disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:ring-destructive/20 aria-invalid:border-destructive", {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground shadow-xs hover:bg-[#505050] disabled:bg-[#EEEEEE] disabled:text-[#999999] data-[loading=true]:bg-[#505050]",
      destructive: "bg-destructive text-white shadow-xs hover:bg-[#bd001a] focus-visible:ring-destructive/20 disabled:bg-destructive/50",
      outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground disabled:bg-background/50 disabled:text-muted-foreground",
      secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 disabled:bg-secondary/50",
      ghost: "hover:bg-accent hover:text-accent-foreground disabled:text-muted-foreground",
      link: "text-primary underline-offset-4 underline font-normal hover:font-medium !p-0 !h-auto disabled:text-muted-foreground"
    },
    size: {
      default: "h-10 px-4 py-2 has-[>svg]:px-3",
      xs: "h-7 rounded-md px-1.5 py-1 text-xs",
      sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
      lg: "h-12 rounded-md px-6 has-[>svg]:px-4",
      icon: "size-9"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
const loadingVariants = cva("rounded-full animate-spin", {
  variants: {
    variant: {
      default: "border-2 border-[#909090] border-t-[#FEFEFE]",
      destructive: "border-2 border-white/30 border-t-white",
      outline: "border-2 border-muted-foreground/30 border-t-muted-foreground",
      secondary: "border-2 border-secondary-foreground/30 border-t-secondary-foreground",
      ghost: "border-2 border-muted-foreground/30 border-t-muted-foreground",
      link: "border-2 border-primary/30 border-t-primary"
    },
    size: {
      default: "w-4 h-4",
      xs: "w-3 h-3",
      sm: "w-3.5 h-3.5",
      lg: "w-5 h-5",
      icon: "w-4 h-4"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function Button(t0) {
  const $ = _c(25);
  let children;
  let className;
  let disabled;
  let props;
  let size;
  let t1;
  let t2;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      loading: t2,
      children,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = props;
    $[5] = size;
    $[6] = t1;
    $[7] = t2;
    $[8] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    props = $[4];
    size = $[5];
    t1 = $[6];
    t2 = $[7];
    variant = $[8];
  }
  const asChild = t1 === undefined ? false : t1;
  const loading = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot : "button";
  let t3;
  if ($[9] !== className || $[10] !== size || $[11] !== variant) {
    t3 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[9] = className;
    $[10] = size;
    $[11] = variant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = disabled || loading;
  let t5;
  if ($[13] !== children || $[14] !== loading || $[15] !== size || $[16] !== variant) {
    t5 = loading ? <div className={cn(loadingVariants({
      variant,
      size
    }))} /> : children;
    $[13] = children;
    $[14] = loading;
    $[15] = size;
    $[16] = variant;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== Comp || $[19] !== loading || $[20] !== props || $[21] !== t3 || $[22] !== t4 || $[23] !== t5) {
    t6 = <Comp data-slot="button" data-loading={loading} className={t3} disabled={t4} {...props}>{t5}</Comp>;
    $[18] = Comp;
    $[19] = loading;
    $[20] = props;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
}
export { Button, buttonVariants };